import { DocShape } from "@eave/shared/types/api";
import { getFileDownloadBaseURL } from "config";
import { DocumentOwnerOption } from "./types";
import { ADDRESS_DELIM } from "./values";

export const docToUrl = (doc: DocShape) => getFileDownloadBaseURL(doc.s3_key);

export const cleanFileName = (fileName: string) => fileName.replaceAll("/", "");

export const docToFileName = (doc: DocShape): string => {
  const { s3_key, filename } = doc;

  if (filename) {
    return filename;
  }

  const s = s3_key.split("/");
  return s[s.length - 1];
};

// Given a blob href and the `download` prop (which controls the filename when downloaded), will auto-initiate a downlaod
export const initiateBlobDownload = (blobURL: string, download: string) => {
  const link = document.createElement("a");
  link.href = blobURL;
  link.download = download;
  link.click();
};

export const documentOwnerOptions = (app): DocumentOwnerOption[] => {
  const employmentOptions = app.employments.map(employment => ({
    label: `Employment: ${employment.name} -- Borrower ${employment.borrower_entity}`,
    value: {
      owner_entity_type: "employment",
      owner_entity_id: employment.id,
    },
  }));
  const additionalIncomeOptions = app.additional_incomes.map(income => ({
    label: `Additional Income: ${income.operator_income_type || income.description} -- Borrower ${
      income.borrower_entity
    }`,
    value: {
      owner_entity_type: "additional_income",
      owner_entity_id: income.id,
    },
  }));
  const ownedRealEstateOptions = app.owned_real_estate.map(realEstate => {
    const line1 = realEstate.address_line1 || realEstate.operator_address_line1;
    const city = realEstate.city || realEstate.operator_city;
    const state = realEstate.state || realEstate.operator_state;
    return {
      label: `Owned Real Estate: ${line1}, ${city}, ${state}`,
      value: {
        owner_entity_type: "owned_real_estate",
        owner_entity_id: realEstate.id,
      },
    };
  });

  const accountOptions = app.accounts
    .filter(acct => !acct.ignored)
    .map(acct => ({
      label: `Account: ${acct.institution} - ${acct.name} - ${acct.number_suffix}`,
      value: {
        owner_entity_type: "account",
        owner_entity_id: acct.id,
      },
    }));

  return [
    ...employmentOptions,
    ...additionalIncomeOptions,
    ...ownedRealEstateOptions,
    ...accountOptions,
  ];
};

export const removeKeysWithEmptyValues = obj => {
  const newObj = {};

  Object.keys(obj).forEach(key => {
    const val = obj[key];

    if (val) {
      newObj[key] = val;
    }
  });

  return newObj;
};

export const addressStrToObj = str => {
  const arr = str.split(ADDRESS_DELIM);

  if (arr.length !== 5) {
    throw new Error("cannot unpack address object from string");
  }

  const obj = {
    address_line1: arr[0],
    address_line2: arr[1],
    city: arr[2],
    state: arr[3],
    postal_code: arr[4],
  };

  return removeKeysWithEmptyValues(obj);
};

export const addressObjToStr = obj => {
  const { address_line1, address_line2, city, state, postal_code } = obj;

  const arr = [address_line1, address_line2, city, state, postal_code];

  if (arr.some(val => val && val.includes(ADDRESS_DELIM))) {
    throw new Error("cannot safely pack address object to string");
  }

  return arr.join(ADDRESS_DELIM);
};

export const respToContentType = (resp: Response) => {
  let ct = resp.headers.get("Content-Type");
  if (ct && ct.length > 0) {
    ct = ct.split(";")[0]; // e.g., "text/plain; charset=utf-8"
  }
  return ct;
};
